'use client';

import { useMemo, useState, FC } from 'react';
import { observer } from 'mobx-react-lite';

import LocaleListVM from '@/react/view-models/LocaleListVM';
import { classes } from '@/next-utils/css-utils/scss-utils';
import { useLocale } from '@/react/hooks/useLocale';
import { Icon, IconSizeProp } from '@/react/components/core-ui/Icon';
import Button from '@/react/components/core-ui/Button';
import { getFlagByCountry } from '@/react/components/core-ui/Icon/getFlagByCountry';
import { Link } from '@/react/components/core-ui/Link';

import { CountryLink } from '../CountryLink';
import { HoverIntent } from '../../utility/HoverIntent';
import { ViewMoreCountries } from '../ViewMoreCountries';
import CountrySearchBox from '../CountrySearchBox';

import S from './styles.base.module.scss';

/**
 * This is the desktop country selector on used in the site header.
 */
export const TopCountrySelector: FC = observer(function TopCountrySelector() {
  const [isOpen, setIsOpen] = useState(false);
  const vm = useMemo(() => new LocaleListVM(), []);

  const locale = useLocale();
  const flagIcon = getFlagByCountry(locale.country);

  const openHandler = (): void => {
    setIsOpen(true);
  };

  const closeHandler = (): void => {
    setIsOpen(false);
    vm.clearFilter();
  };

  return (
    <HoverIntent
      onHoverIn={openHandler}
      onHoverOut={closeHandler}
      activatedByFocus={false}
      deactivatedByBlur={false}
    >
      <Button className={S.link} variant="text">
        <Icon
          className={classes(S.icon, S.headerFlag)}
          icon={flagIcon}
          size={IconSizeProp.SizeLG}
        />
      </Button>
      {isOpen && (
        <div className={S.popoverContainer}>
          <CountrySearchBox vm={vm} />
          {vm.filteredSites.map((item) => {
            return (
              <div className={S.link} key={item.id}>
                <CountryLink
                  link={item.url}
                  name={item.name}
                  flag={item.icon}
                />
              </div>
            );
          })}
          <div className={S.viewMore}>
            <Link
              className={S.viewMoreCountries}
              href="/choose-country"
              variant="underline"
            >
              <ViewMoreCountries />
            </Link>
          </div>
        </div>
      )}
    </HoverIntent>
  );
});
